import { Bot, FlaskConical, Pencil, Power } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/problemDetails'
import { Badge, Button, Card, CardContent, CardHeader, toast } from '@/components/ui'
import { PausedForCreditsBadge } from '@/features/billing/components/PausedForCredits'

import { agentModeMeta, agentStatusBadgeVariant, agentStatusLabelKey } from '../modeMeta'
import { useSetAgentStatus } from '../queries'
import { isKnownAgentMode, type AgentSummary } from '../types'
import { AgentModeControl } from './AgentModeControl'
import { AgentPlayground } from './AgentPlayground'
import { EditAgentDialog } from './EditAgentDialog'

export interface AgentCardProps {
  readonly tenantId: string
  readonly agent: AgentSummary
  /** Quando ha um unico agente, o teste ja aparece aberto (menos um clique para a primeira experiencia). */
  readonly defaultTestOpen?: boolean
}

/**
 * Card de um agente: nome + status, o modo atual sempre visivel como Badge, o controle de troca de
 * modo e um Playground embutido para conversar com o agente antes de ativar. Vocabulario neutro.
 */
export function AgentCard({ tenantId, agent, defaultTestOpen = false }: AgentCardProps) {
  const { t } = useTranslation()
  const [testOpen, setTestOpen] = useState(defaultTestOpen)
  const [editOpen, setEditOpen] = useState(false)
  const playgroundId = useId()
  const setStatus = useSetAgentStatus(tenantId)

  const statusKey = agentStatusLabelKey(agent.status)
  // Chamar o type guard direto no ternario narrowa `agent.mode` para AgentModeValue no ramo verdadeiro.
  const modeMeta = isKnownAgentMode(agent.mode) ? agentModeMeta(agent.mode) : null
  const active = agent.status === 'Active'

  const toggleStatus = () => {
    setStatus.mutate(
      { agentId: agent.id, enabled: !active },
      {
        onSuccess: () => {
          toast.success(active ? t('agents.toast.disabled') : t('agents.toast.enabled'))
        },
        onError: (error) => {
          toast.error(error instanceof ApiError ? error.problem.title : t('agents.actionFailed'))
        },
      },
    )
  }

  return (
    <Card>
      {editOpen ? (
        <EditAgentDialog
          tenantId={tenantId}
          agent={agent}
          onClose={() => {
            setEditOpen(false)
          }}
        />
      ) : null}
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-action-primary/10 text-action-primary"
            >
              <Bot className="h-5 w-5" />
            </span>
            <div className="flex flex-col gap-1">
              <h3 className="text-base font-semibold text-foreground-primary">{agent.name}</h3>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-xs text-foreground-muted">
                  {t('agents.card.statusLabel')}
                </span>
                <Badge variant={agentStatusBadgeVariant(agent.status)}>
                  {statusKey !== null ? t(statusKey) : agent.status}
                </Badge>
              </span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            {/* Modo atual sempre visivel (docs/design/01 s11). */}
            <span className="inline-flex items-center gap-1.5">
              <span className="text-xs text-foreground-muted">{t('agents.mode.currentLabel')}</span>
              <Badge variant={modeMeta?.badgeVariant ?? 'neutral'}>
                {modeMeta !== null ? t(modeMeta.labelKey) : agent.mode}
              </Badge>
              <PausedForCreditsBadge tenantId={tenantId} />
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  setEditOpen(true)
                }}
              >
                <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                {t('agents.card.edit')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5"
                disabled={setStatus.isPending}
                onClick={toggleStatus}
              >
                <Power aria-hidden="true" className="h-3.5 w-3.5" />
                {active ? t('agents.card.disable') : t('agents.card.enable')}
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        <AgentModeControl tenantId={tenantId} agent={agent} />

        <div className="flex flex-col gap-3 border-t border-border-default pt-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-semibold text-foreground-primary">
                {t('agents.playground.title')}
              </p>
              <p className="text-xs text-foreground-muted">{t('agents.playground.subtitle')}</p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="gap-1.5"
              aria-expanded={testOpen}
              aria-controls={playgroundId}
              onClick={() => {
                setTestOpen((open) => !open)
              }}
            >
              <FlaskConical aria-hidden="true" className="h-4 w-4" />
              {testOpen ? t('agents.card.hideTestCta') : t('agents.card.testCta')}
            </Button>
          </div>

          {testOpen ? (
            <div id={playgroundId}>
              <AgentPlayground tenantId={tenantId} agentId={agent.id} />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
